import React from 'react';
import { Box, Typography } from '@mui/material';
import { InfiniteScroll, ZSpinner } from '@marageti/z4-lib';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';
import { Ticket, TicketStatus } from '@marageti/z4-sdk/lib/tickets';
import TicketEditModal from '../modals/ticket-edit-modal';
import { EditableTicket, TicketFormData, AssigneeOption } from '../../pages/tickets/options';
import TicketCloseModal, {
  TicketCloseFormData,
} from '../modals/ticket-close-modal';
import TicketReassignModal, {
  TicketReassignFormData,
} from '../modals/ticket-reassign-modal';
import TicketDeleteModal from '../modals/ticket-delete-modal';
import TicketCard from '../list-cards/ticket-cards';

type TicketListProps = {
  tickets: Ticket[];
  totalHits: number;
  isLoading: boolean;
  error: ApiError | undefined;
  hasMore: boolean;
  loadMore: () => void;
  onEditTicket: (ticket: Ticket) => void;
  onReassignTicket: (ticket: Ticket) => void;
  onDeleteTicket: (ticket: Ticket) => void;
  onStatusChange: (ticketId: string, status: TicketStatus) => void;
  isModalOpen: boolean;
  editingTicket?: EditableTicket;
  assignees: AssigneeOption[];
  defaultAssigneeIds?: string[];
  onCloseModal: () => void;
  onSaveTicket: (data: TicketFormData) => Promise<void>;
  isCloseModalOpen: boolean;
  onCloseCloseModal: () => void;
  onSaveClose: (data: TicketCloseFormData) => Promise<void>;
  isReassignModalOpen: boolean;
  reassigningTicketAssigneeIds: string[];
  onCloseReassignModal: () => void;
  onSaveReassign: (data: TicketReassignFormData) => Promise<void>;
  isDeleteModalOpen: boolean;
  onCloseDeleteModal: () => void;
  onConfirmDelete: () => Promise<void>;
};

const TicketList = ({
  tickets,
  totalHits,
  isLoading,
  error,
  hasMore,
  loadMore,
  onEditTicket,
  onReassignTicket,
  onDeleteTicket,
  onStatusChange,
  isModalOpen,
  editingTicket,
  assignees,
  defaultAssigneeIds,
  onCloseModal,
  onSaveTicket,
  isCloseModalOpen,
  onCloseCloseModal,
  onSaveClose,
  isReassignModalOpen,
  reassigningTicketAssigneeIds,
  onCloseReassignModal,
  onSaveReassign,
  isDeleteModalOpen,
  onCloseDeleteModal,
  onConfirmDelete,
}: TicketListProps) => {
  const formattedCount = new Intl.NumberFormat('en-US').format(totalHits);

  return (
    <>
      {!isLoading && tickets.length > 0 && (
        <Typography component="p" variant="h4" sx={{ mb: 1.5 }}>
          {`${formattedCount} Results`}
        </Typography>
      )}

      {isLoading && tickets.length === 0 ? (
        <Box display="flex" justifyContent="center" py={4}>
          <ZSpinner />
        </Box>
      ) : (
        <InfiniteScroll
          error={error}
          hasMoreItems={hasMore}
          isLoading={isLoading}
          loadMoreItems={loadMore}
          noItemsText="There are no items to display."
          stackProps={{ spacing: 1 }}
        >
          {tickets.map((ticket) => (
            <TicketCard
              key={ticket.id}
              ticket={ticket}
              onEdit={() => onEditTicket(ticket)}
              onReassign={() => onReassignTicket(ticket)}
              onDelete={() => onDeleteTicket(ticket)}
              onStatusChange={onStatusChange}
            />
          ))}
        </InfiniteScroll>
      )}

      <TicketEditModal
        open={isModalOpen}
        onClose={onCloseModal}
        onSave={onSaveTicket}
        assignees={assignees}
        ticket={editingTicket}
        defaultValues={{
          assigneeIds: defaultAssigneeIds,
        }}
        isNew={!editingTicket}
      />

      <TicketCloseModal
        open={isCloseModalOpen}
        onClose={onCloseCloseModal}
        onSave={onSaveClose}
      />

      <TicketReassignModal
        open={isReassignModalOpen}
        onClose={onCloseReassignModal}
        onSave={onSaveReassign}
        assignees={assignees}
        currentAssigneeIds={reassigningTicketAssigneeIds}
      />

      <TicketDeleteModal
        open={isDeleteModalOpen}
        onClose={onCloseDeleteModal}
        onConfirm={onConfirmDelete}
      />
    </>
  );
};

export default TicketList;
